import { useCallback, useRef } from "react";

type SoundType = "achievement" | "celebration" | "success" | "pop" | "fanfare" | "bail" | "ghost" | "excuse" | "whoosh" | "click" | "notification";

export type { SoundType };

// Check if sound is enabled from localStorage
const isSoundEnabled = () => {
  const stored = localStorage.getItem("soundEnabled");
  return stored === null ? true : stored === "true";
};

const useSoundEffects = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!isSoundEnabled()) return null;
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Create a simple convolver for reverb-like effect
  const createReverb = useCallback((ctx: AudioContext, duration: number = 1.5, decay: number = 2) => {
    const sampleRate = ctx.sampleRate;
    const length = sampleRate * duration;
    const impulse = ctx.createBuffer(2, length, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    
    const convolver = ctx.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  }, []);

  // Create a warm, natural-sounding oscillator with harmonics
  const createRichTone = useCallback((
    ctx: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
    type: OscillatorType = "sine",
    harmonics: number[] = [1, 0.5, 0.25]
  ) => {
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 4000;
    filter.Q.value = 0.5;

    harmonics.forEach((amplitude, i) => {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      
      osc.type = type;
      osc.frequency.setValueAtTime(frequency * (i + 1), startTime);
      oscGain.gain.setValueAtTime(amplitude * 0.15, startTime);
      
      osc.connect(oscGain);
      oscGain.connect(filter);
      
      osc.start(startTime);
      osc.stop(startTime + duration);
    });

    filter.connect(gainNode);
    return gainNode;
  }, []);

  // Haptic feedback patterns
  const triggerHaptic = useCallback((pattern: "light" | "medium" | "heavy" | "celebration") => {
    if (!("vibrate" in navigator)) return;

    const patterns: Record<string, number | number[]> = {
      light: 10,
      medium: 30,
      heavy: 50,
      celebration: [50, 30, 50, 30, 100, 50, 150],
    };

    try {
      navigator.vibrate(patterns[pattern]);
    } catch {
      // Vibration not supported or failed
    }
  }, []);

  // Rich achievement unlock sound with shimmer
  const playAchievementSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    // Master gain and reverb
    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 1.2, 2.5);
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    
    masterGain.gain.setValueAtTime(0.4, now);
    dryGain.gain.setValueAtTime(0.7, now);
    wetGain.gain.setValueAtTime(0.3, now);
    
    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(wetGain);
    dryGain.connect(ctx.destination);
    wetGain.connect(ctx.destination);

    // Warm chord arpeggio with layered tones
    const notes = [
      { freq: 523.25, time: 0 },      // C5
      { freq: 659.25, time: 0.08 },   // E5
      { freq: 783.99, time: 0.16 },   // G5
      { freq: 1046.50, time: 0.24 },  // C6
    ];
    
    notes.forEach(({ freq, time }) => {
      // Main tone with harmonics
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(3000, now + time);
      filter.frequency.exponentialRampToValueAtTime(1500, now + time + 0.4);
      filter.Q.value = 1;
      
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(freq, now + time);
      
      osc2.type = "triangle";
      osc2.frequency.setValueAtTime(freq * 2, now + time);
      
      const osc2Gain = ctx.createGain();
      osc2Gain.gain.setValueAtTime(0.15, now + time);
      
      // Smooth ADSR envelope
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.25, now + time + 0.03);
      gain.gain.setValueAtTime(0.2, now + time + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.5);
      
      osc1.connect(filter);
      osc2.connect(osc2Gain);
      osc2Gain.connect(filter);
      filter.connect(gain);
      gain.connect(masterGain);
      
      osc1.start(now + time);
      osc2.start(now + time);
      osc1.stop(now + time + 0.6);
      osc2.stop(now + time + 0.6);
    });

    // Shimmer layer with modulated high frequencies
    const shimmerOsc = ctx.createOscillator();
    const shimmerGain = ctx.createGain();
    const shimmerFilter = ctx.createBiquadFilter();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    
    shimmerOsc.type = "sine";
    shimmerOsc.frequency.setValueAtTime(2093, now + 0.3);
    
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(12, now + 0.3);
    lfoGain.gain.setValueAtTime(200, now + 0.3);
    
    shimmerFilter.type = "bandpass";
    shimmerFilter.frequency.setValueAtTime(3000, now + 0.3);
    shimmerFilter.Q.value = 2;
    
    shimmerGain.gain.setValueAtTime(0, now + 0.3);
    shimmerGain.gain.linearRampToValueAtTime(0.08, now + 0.35);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    
    lfo.connect(lfoGain);
    lfoGain.connect(shimmerOsc.frequency);
    shimmerOsc.connect(shimmerFilter);
    shimmerFilter.connect(shimmerGain);
    shimmerGain.connect(masterGain);
    
    lfo.start(now + 0.3);
    shimmerOsc.start(now + 0.3);
    lfo.stop(now + 0.9);
    shimmerOsc.stop(now + 0.9);

    triggerHaptic("medium");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Dynamic celebration fanfare with full arrangement
  const playCelebrationSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 2, 2);
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    
    masterGain.gain.setValueAtTime(0.35, now);
    dryGain.gain.setValueAtTime(0.75, now);
    wetGain.gain.setValueAtTime(0.25, now);
    
    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(wetGain);
    dryGain.connect(ctx.destination);
    wetGain.connect(ctx.destination);

    // Brass-like fanfare melody
    const melody = [
      { freq: 523.25, time: 0, duration: 0.12 },
      { freq: 659.25, time: 0.12, duration: 0.12 },
      { freq: 783.99, time: 0.24, duration: 0.12 },
      { freq: 1046.50, time: 0.36, duration: 0.25 },
      { freq: 783.99, time: 0.65, duration: 0.08 },
      { freq: 1046.50, time: 0.75, duration: 0.45 },
    ];

    melody.forEach(({ freq, time, duration }) => {
      // Layered brass-like tone
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const osc3 = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(2500, now + time);
      filter.frequency.linearRampToValueAtTime(1800, now + time + duration);
      filter.Q.value = 0.7;
      
      osc1.type = "sawtooth";
      osc1.frequency.setValueAtTime(freq, now + time);
      
      osc2.type = "square";
      osc2.frequency.setValueAtTime(freq, now + time);
      
      osc3.type = "sine";
      osc3.frequency.setValueAtTime(freq * 2, now + time);
      
      const osc1Gain = ctx.createGain();
      const osc2Gain = ctx.createGain();
      const osc3Gain = ctx.createGain();
      osc1Gain.gain.setValueAtTime(0.12, now + time);
      osc2Gain.gain.setValueAtTime(0.06, now + time);
      osc3Gain.gain.setValueAtTime(0.08, now + time);
      
      // Attack-decay envelope
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.3, now + time + 0.02);
      gain.gain.setValueAtTime(0.25, now + time + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + duration + 0.1);
      
      osc1.connect(osc1Gain);
      osc2.connect(osc2Gain);
      osc3.connect(osc3Gain);
      osc1Gain.connect(filter);
      osc2Gain.connect(filter);
      osc3Gain.connect(filter);
      filter.connect(gain);
      gain.connect(masterGain);
      
      osc1.start(now + time);
      osc2.start(now + time);
      osc3.start(now + time);
      osc1.stop(now + time + duration + 0.15);
      osc2.stop(now + time + duration + 0.15);
      osc3.stop(now + time + duration + 0.15);
    });

    // Punchy kick drum hits
    [0, 0.36, 0.75].forEach((time) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      filter.type = "lowpass";
      filter.frequency.value = 200;
      
      osc.type = "sine";
      osc.frequency.setValueAtTime(180, now + time);
      osc.frequency.exponentialRampToValueAtTime(40, now + time + 0.08);
      
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.5, now + time + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.15);
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(masterGain);
      
      osc.start(now + time);
      osc.stop(now + time + 0.2);
    });

    // Subtle cymbal shimmer
    const noiseLength = ctx.sampleRate * 0.6;
    const noiseBuffer = ctx.createBuffer(1, noiseLength, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLength; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    
    const noiseGain = ctx.createGain();
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "highpass";
    noiseFilter.frequency.value = 8000;
    
    noiseGain.gain.setValueAtTime(0, now + 0.36);
    noiseGain.gain.linearRampToValueAtTime(0.04, now + 0.38);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.9);
    
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(masterGain);
    
    noise.start(now + 0.36);
    noise.stop(now + 1);

    triggerHaptic("celebration");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Soft, satisfying pop sound
  const playPopSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.4, now);
    masterGain.connect(ctx.destination);

    // Layered pop with body
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(400, now + 0.08);
    filter.Q.value = 2;
    
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(800, now);
    osc1.frequency.exponentialRampToValueAtTime(300, now + 0.06);
    
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(1200, now);
    osc2.frequency.exponentialRampToValueAtTime(400, now + 0.04);
    
    const osc2Gain = ctx.createGain();
    osc2Gain.gain.setValueAtTime(0.3, now);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.25, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    
    osc1.connect(filter);
    osc2.connect(osc2Gain);
    osc2Gain.connect(filter);
    filter.connect(gain);
    gain.connect(masterGain);
    
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.12);
    osc2.stop(now + 0.12);

    triggerHaptic("light");
  }, [getAudioContext, triggerHaptic]);

  // Sneaky, playful bail sound
  const playBailSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.35, now);
    masterGain.connect(ctx.destination);

    // Descending whistle with vibrato
    const whistle = ctx.createOscillator();
    const whistleGain = ctx.createGain();
    const whistleFilter = ctx.createBiquadFilter();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    
    whistleFilter.type = "lowpass";
    whistleFilter.frequency.setValueAtTime(2000, now);
    whistleFilter.Q.value = 1;
    
    whistle.type = "sine";
    whistle.frequency.setValueAtTime(900, now);
    whistle.frequency.exponentialRampToValueAtTime(250, now + 0.35);
    
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(6, now);
    lfoGain.gain.setValueAtTime(15, now);
    
    whistleGain.gain.setValueAtTime(0, now);
    whistleGain.gain.linearRampToValueAtTime(0.2, now + 0.03);
    whistleGain.gain.setValueAtTime(0.15, now + 0.1);
    whistleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    
    lfo.connect(lfoGain);
    lfoGain.connect(whistle.frequency);
    whistle.connect(whistleFilter);
    whistleFilter.connect(whistleGain);
    whistleGain.connect(masterGain);
    
    lfo.start(now);
    whistle.start(now);
    lfo.stop(now + 0.45);
    whistle.stop(now + 0.45);

    // Soft tiptoeing steps
    [0.12, 0.24, 0.36].forEach((time, i) => {
      const tap = ctx.createOscillator();
      const tapGain = ctx.createGain();
      const tapFilter = ctx.createBiquadFilter();
      
      tapFilter.type = "lowpass";
      tapFilter.frequency.value = 400;
      
      tap.type = "sine";
      tap.frequency.setValueAtTime(120 - i * 10, now + time);
      tap.frequency.exponentialRampToValueAtTime(60, now + time + 0.04);
      
      tapGain.gain.setValueAtTime(0, now + time);
      tapGain.gain.linearRampToValueAtTime(0.12, now + time + 0.005);
      tapGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.06);
      
      tap.connect(tapFilter);
      tapFilter.connect(tapGain);
      tapGain.connect(masterGain);
      
      tap.start(now + time);
      tap.stop(now + time + 0.1);
    });

    triggerHaptic("medium");
  }, [getAudioContext, triggerHaptic]);

  // Ethereal ghost sound with depth
  const playGhostSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 2, 1.5);
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    
    masterGain.gain.setValueAtTime(0.3, now);
    dryGain.gain.setValueAtTime(0.5, now);
    wetGain.gain.setValueAtTime(0.5, now);
    
    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(wetGain);
    dryGain.connect(ctx.destination);
    wetGain.connect(ctx.destination);

    // Breathy noise layer
    const noiseLength = ctx.sampleRate * 0.6;
    const noiseBuffer = ctx.createBuffer(1, noiseLength, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLength; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    
    const noiseGain = ctx.createGain();
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(2500, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(800, now + 0.5);
    noiseFilter.Q.value = 2;
    
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(0.08, now + 0.1);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(masterGain);
    
    noise.start(now);
    noise.stop(now + 0.6);

    // Eerie descending tone with chorus effect
    [0, 0.01, 0.02].forEach((detune, i) => {
      const ghost = ctx.createOscillator();
      const ghostGain = ctx.createGain();
      const ghostFilter = ctx.createBiquadFilter();
      
      ghostFilter.type = "lowpass";
      ghostFilter.frequency.setValueAtTime(1200, now);
      ghostFilter.frequency.exponentialRampToValueAtTime(400, now + 0.4);
      
      ghost.type = "sine";
      ghost.frequency.setValueAtTime(600 + detune * 200, now);
      ghost.frequency.exponentialRampToValueAtTime(300 + detune * 100, now + 0.4);
      ghost.detune.setValueAtTime(i * 8 - 8, now);
      
      ghostGain.gain.setValueAtTime(0, now);
      ghostGain.gain.linearRampToValueAtTime(0.06, now + 0.08);
      ghostGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      
      ghost.connect(ghostFilter);
      ghostFilter.connect(ghostGain);
      ghostGain.connect(masterGain);
      
      ghost.start(now);
      ghost.stop(now + 0.5);
    });

    triggerHaptic("light");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Playful excuse bubble sound
  const playExcuseSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.35, now);
    masterGain.connect(ctx.destination);

    // Bubble rise and pop
    const bubble = ctx.createOscillator();
    const bubbleGain = ctx.createGain();
    const bubbleFilter = ctx.createBiquadFilter();
    
    bubbleFilter.type = "bandpass";
    bubbleFilter.frequency.setValueAtTime(800, now);
    bubbleFilter.Q.value = 3;
    
    bubble.type = "sine";
    bubble.frequency.setValueAtTime(300, now);
    bubble.frequency.exponentialRampToValueAtTime(600, now + 0.08);
    bubble.frequency.exponentialRampToValueAtTime(1000, now + 0.12);
    bubble.frequency.exponentialRampToValueAtTime(400, now + 0.18);
    
    bubbleGain.gain.setValueAtTime(0, now);
    bubbleGain.gain.linearRampToValueAtTime(0.2, now + 0.02);
    bubbleGain.gain.setValueAtTime(0.18, now + 0.1);
    bubbleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    
    bubble.connect(bubbleFilter);
    bubbleFilter.connect(bubbleGain);
    bubbleGain.connect(masterGain);
    
    bubble.start(now);
    bubble.stop(now + 0.25);

    // Soft boing with natural decay
    const boing = ctx.createOscillator();
    const boingGain = ctx.createGain();
    const boingFilter = ctx.createBiquadFilter();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    
    boingFilter.type = "lowpass";
    boingFilter.frequency.setValueAtTime(1500, now + 0.12);
    boingFilter.frequency.exponentialRampToValueAtTime(600, now + 0.35);
    
    boing.type = "triangle";
    boing.frequency.setValueAtTime(500, now + 0.12);
    boing.frequency.exponentialRampToValueAtTime(280, now + 0.3);
    
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(8, now + 0.12);
    lfoGain.gain.setValueAtTime(20, now + 0.12);
    
    boingGain.gain.setValueAtTime(0, now + 0.12);
    boingGain.gain.linearRampToValueAtTime(0.12, now + 0.14);
    boingGain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
    
    lfo.connect(lfoGain);
    lfoGain.connect(boing.frequency);
    boing.connect(boingFilter);
    boingFilter.connect(boingGain);
    boingGain.connect(masterGain);
    
    lfo.start(now + 0.12);
    boing.start(now + 0.12);
    lfo.stop(now + 0.4);
    boing.stop(now + 0.4);

    triggerHaptic("light");
  }, [getAudioContext, triggerHaptic]);

  // Smooth whoosh sound
  const playWhooshSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.4, now);
    masterGain.connect(ctx.destination);

    // Shaped noise whoosh
    const noiseLength = ctx.sampleRate * 0.35;
    const noiseBuffer = ctx.createBuffer(1, noiseLength, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLength; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    
    const noiseGain = ctx.createGain();
    const highpass = ctx.createBiquadFilter();
    const lowpass = ctx.createBiquadFilter();
    
    highpass.type = "highpass";
    highpass.frequency.setValueAtTime(300, now);
    highpass.frequency.exponentialRampToValueAtTime(2000, now + 0.15);
    highpass.frequency.exponentialRampToValueAtTime(400, now + 0.3);
    
    lowpass.type = "lowpass";
    lowpass.frequency.setValueAtTime(4000, now);
    lowpass.frequency.exponentialRampToValueAtTime(8000, now + 0.1);
    lowpass.frequency.exponentialRampToValueAtTime(2000, now + 0.3);
    
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(0.18, now + 0.08);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
    
    noise.connect(highpass);
    highpass.connect(lowpass);
    lowpass.connect(noiseGain);
    noiseGain.connect(masterGain);
    
    noise.start(now);
    noise.stop(now + 0.35);

    // Subtle pitch sweep for movement feel
    const sweep = ctx.createOscillator();
    const sweepGain = ctx.createGain();
    const sweepFilter = ctx.createBiquadFilter();
    
    sweepFilter.type = "bandpass";
    sweepFilter.frequency.setValueAtTime(1000, now);
    sweepFilter.Q.value = 1;
    
    sweep.type = "sawtooth";
    sweep.frequency.setValueAtTime(200, now);
    sweep.frequency.exponentialRampToValueAtTime(800, now + 0.15);
    sweep.frequency.exponentialRampToValueAtTime(150, now + 0.3);
    
    sweepGain.gain.setValueAtTime(0, now);
    sweepGain.gain.linearRampToValueAtTime(0.03, now + 0.05);
    sweepGain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    
    sweep.connect(sweepFilter);
    sweepFilter.connect(sweepGain);
    sweepGain.connect(masterGain);
    
    sweep.start(now);
    sweep.stop(now + 0.32);

    triggerHaptic("medium");
  }, [getAudioContext, triggerHaptic]);

  // Rich success chime with warmth
  const playSuccessSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 0.8, 3);
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    
    masterGain.gain.setValueAtTime(0.35, now);
    dryGain.gain.setValueAtTime(0.8, now);
    wetGain.gain.setValueAtTime(0.2, now);
    
    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(wetGain);
    dryGain.connect(ctx.destination);
    wetGain.connect(ctx.destination);

    // Major chord arpeggio with harmonics
    const notes = [
      { freq: 440, time: 0 },      // A4
      { freq: 554.37, time: 0.07 }, // C#5
      { freq: 659.25, time: 0.14 }, // E5
    ];
    
    notes.forEach(({ freq, time }) => {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(2500, now + time);
      filter.frequency.exponentialRampToValueAtTime(1200, now + time + 0.35);
      filter.Q.value = 0.7;
      
      osc1.type = "sine";
      osc1.frequency.setValueAtTime(freq, now + time);
      
      osc2.type = "triangle";
      osc2.frequency.setValueAtTime(freq * 2, now + time);
      
      const osc2Gain = ctx.createGain();
      osc2Gain.gain.setValueAtTime(0.2, now + time);
      
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.22, now + time + 0.015);
      gain.gain.setValueAtTime(0.18, now + time + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.4);
      
      osc1.connect(filter);
      osc2.connect(osc2Gain);
      osc2Gain.connect(filter);
      filter.connect(gain);
      gain.connect(masterGain);
      
      osc1.start(now + time);
      osc2.start(now + time);
      osc1.stop(now + time + 0.45);
      osc2.stop(now + time + 0.45);
    });

    triggerHaptic("medium");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Distinct notification chime - pleasant two-note bell sound
  const playNotificationSound = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    // Master gain and reverb
    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 0.8, 2.5);
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    
    masterGain.gain.setValueAtTime(0.3, now);
    dryGain.gain.setValueAtTime(0.7, now);
    wetGain.gain.setValueAtTime(0.3, now);
    
    masterGain.connect(dryGain);
    masterGain.connect(reverb);
    reverb.connect(wetGain);
    dryGain.connect(ctx.destination);
    wetGain.connect(ctx.destination);

    // Two-note chime (high-low pattern like a doorbell)
    const chimeNotes = [
      { freq: 1046.50, time: 0 },      // C6
      { freq: 783.99, time: 0.12 },    // G5
    ];
    
    chimeNotes.forEach(({ freq, time }) => {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      // Use sine for pure bell-like tone
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + time);
      
      // Soft lowpass for warmth
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(3000, now + time);
      filter.Q.value = 1;
      
      // Bell-like envelope - quick attack, medium sustain, gentle decay
      oscGain.gain.setValueAtTime(0, now + time);
      oscGain.gain.linearRampToValueAtTime(0.35, now + time + 0.01);
      oscGain.gain.setValueAtTime(0.3, now + time + 0.02);
      oscGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.5);
      
      // Add subtle harmonic for richness
      const harmonic = ctx.createOscillator();
      const harmonicGain = ctx.createGain();
      harmonic.type = "triangle";
      harmonic.frequency.setValueAtTime(freq * 2, now + time);
      harmonicGain.gain.setValueAtTime(0.08, now + time);
      harmonicGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.3);
      
      osc.connect(filter);
      filter.connect(oscGain);
      oscGain.connect(masterGain);
      
      harmonic.connect(harmonicGain);
      harmonicGain.connect(masterGain);
      
      osc.start(now + time);
      osc.stop(now + time + 0.55);
      harmonic.start(now + time);
      harmonic.stop(now + time + 0.35);
    });

    triggerHaptic("light");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Theme-specific celebration sounds
  type CelebrationTheme = "classic" | "gold" | "neon" | "cosmic" | "fire" | "ice";

  // Golden VIP - Majestic royal fanfare with bells
  const playGoldCelebration = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 2.5, 2);
    masterGain.gain.setValueAtTime(0.35, now);
    masterGain.connect(reverb);
    reverb.connect(ctx.destination);
    masterGain.connect(ctx.destination);

    // Royal trumpet fanfare
    const trumpetNotes = [
      { freq: 523.25, time: 0, duration: 0.15 },
      { freq: 659.25, time: 0.15, duration: 0.15 },
      { freq: 783.99, time: 0.30, duration: 0.20 },
      { freq: 1046.50, time: 0.55, duration: 0.40 },
    ];

    trumpetNotes.forEach(({ freq, time, duration }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(freq, now + time);
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.15, now + time + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + duration);
      osc.connect(gain);
      gain.connect(masterGain);
      osc.start(now + time);
      osc.stop(now + time + duration + 0.1);
    });

    // Bell chimes
    [0.4, 0.55, 0.7].forEach((time) => {
      const bell = ctx.createOscillator();
      const bellGain = ctx.createGain();
      bell.type = "sine";
      bell.frequency.setValueAtTime(2093, now + time);
      bellGain.gain.setValueAtTime(0, now + time);
      bellGain.gain.linearRampToValueAtTime(0.1, now + time + 0.01);
      bellGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.4);
      bell.connect(bellGain);
      bellGain.connect(masterGain);
      bell.start(now + time);
      bell.stop(now + time + 0.5);
    });

    triggerHaptic("celebration");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Neon Nights - Electronic synth arpeggio
  const playNeonCelebration = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.3, now);
    masterGain.connect(ctx.destination);

    // Synth arpeggio
    const notes = [392, 523.25, 659.25, 783.99, 1046.50, 783.99, 659.25, 523.25];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      osc.type = "square";
      osc.frequency.setValueAtTime(freq, now + i * 0.08);
      
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(3000, now + i * 0.08);
      filter.frequency.exponentialRampToValueAtTime(800, now + i * 0.08 + 0.1);
      
      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.08 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.15);
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(masterGain);
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.2);
    });

    // Bass drop
    const bass = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bass.type = "sine";
    bass.frequency.setValueAtTime(80, now + 0.64);
    bass.frequency.exponentialRampToValueAtTime(40, now + 0.9);
    bassGain.gain.setValueAtTime(0, now + 0.64);
    bassGain.gain.linearRampToValueAtTime(0.4, now + 0.65);
    bassGain.gain.exponentialRampToValueAtTime(0.001, now + 1);
    bass.connect(bassGain);
    bassGain.connect(masterGain);
    bass.start(now + 0.64);
    bass.stop(now + 1.1);

    triggerHaptic("celebration");
  }, [getAudioContext, triggerHaptic]);

  // Cosmic Dreams - Ethereal space sounds
  const playCosmicCelebration = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 3, 1.5);
    masterGain.gain.setValueAtTime(0.3, now);
    masterGain.connect(reverb);
    reverb.connect(ctx.destination);

    // Ethereal pads
    const padFreqs = [261.63, 329.63, 392];
    padFreqs.forEach((freq) => {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      
      osc1.type = "sine";
      osc2.type = "triangle";
      osc1.frequency.setValueAtTime(freq, now);
      osc2.frequency.setValueAtTime(freq * 1.005, now);
      
      lfo.type = "sine";
      lfo.frequency.setValueAtTime(0.5, now);
      lfoGain.gain.setValueAtTime(5, now);
      lfo.connect(lfoGain);
      lfoGain.connect(osc1.frequency);
      
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.3);
      gain.gain.setValueAtTime(0.06, now + 0.8);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
      
      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(masterGain);
      osc1.start(now);
      osc2.start(now);
      lfo.start(now);
      osc1.stop(now + 1.6);
      osc2.stop(now + 1.6);
      lfo.stop(now + 1.6);
    });

    // Sparkle chimes
    [0.2, 0.4, 0.6, 0.9, 1.1].forEach((time) => {
      const chime = ctx.createOscillator();
      const chimeGain = ctx.createGain();
      chime.type = "sine";
      chime.frequency.setValueAtTime(2000 + Math.random() * 1000, now + time);
      chimeGain.gain.setValueAtTime(0, now + time);
      chimeGain.gain.linearRampToValueAtTime(0.06, now + time + 0.01);
      chimeGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.3);
      chime.connect(chimeGain);
      chimeGain.connect(masterGain);
      chime.start(now + time);
      chime.stop(now + time + 0.4);
    });

    triggerHaptic("celebration");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Blazing Hot - Fire crackle and intense whoosh
  const playFireCelebration = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.35, now);
    masterGain.connect(ctx.destination);

    // Fire whoosh
    const noiseLength = ctx.sampleRate * 0.8;
    const noiseBuffer = ctx.createBuffer(1, noiseLength, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLength; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    const noiseGain = ctx.createGain();
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(500, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(2000, now + 0.3);
    noiseFilter.frequency.exponentialRampToValueAtTime(300, now + 0.8);
    noiseFilter.Q.value = 1;
    
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(0.25, now + 0.1);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(masterGain);
    noise.start(now);
    noise.stop(now + 0.9);

    // Crackle pops
    [0.1, 0.2, 0.35, 0.5, 0.65].forEach((time) => {
      const pop = ctx.createOscillator();
      const popGain = ctx.createGain();
      pop.type = "sine";
      pop.frequency.setValueAtTime(400 + Math.random() * 200, now + time);
      pop.frequency.exponentialRampToValueAtTime(100, now + time + 0.03);
      popGain.gain.setValueAtTime(0, now + time);
      popGain.gain.linearRampToValueAtTime(0.2, now + time + 0.005);
      popGain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.05);
      pop.connect(popGain);
      popGain.connect(masterGain);
      pop.start(now + time);
      pop.stop(now + time + 0.1);
    });

    // Intense rising tone
    const rise = ctx.createOscillator();
    const riseGain = ctx.createGain();
    rise.type = "sawtooth";
    rise.frequency.setValueAtTime(200, now);
    rise.frequency.exponentialRampToValueAtTime(800, now + 0.4);
    riseGain.gain.setValueAtTime(0, now);
    riseGain.gain.linearRampToValueAtTime(0.1, now + 0.2);
    riseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    rise.connect(riseGain);
    riseGain.connect(masterGain);
    rise.start(now);
    rise.stop(now + 0.6);

    triggerHaptic("celebration");
  }, [getAudioContext, triggerHaptic]);

  // Ice Cold - Crystal chimes and frozen sounds
  const playIceCelebration = useCallback(() => {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    const reverb = createReverb(ctx, 2, 3);
    masterGain.gain.setValueAtTime(0.3, now);
    masterGain.connect(reverb);
    reverb.connect(ctx.destination);

    // Crystal chimes cascade
    const chimeFreqs = [2093, 2637, 3136, 3520, 4186, 3520, 3136, 2637];
    chimeFreqs.forEach((freq, i) => {
      const chime = ctx.createOscillator();
      const chimeGain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      chime.type = "sine";
      chime.frequency.setValueAtTime(freq, now + i * 0.1);
      
      filter.type = "highpass";
      filter.frequency.value = 1500;
      
      chimeGain.gain.setValueAtTime(0, now + i * 0.1);
      chimeGain.gain.linearRampToValueAtTime(0.12, now + i * 0.1 + 0.01);
      chimeGain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.5);
      
      chime.connect(filter);
      filter.connect(chimeGain);
      chimeGain.connect(masterGain);
      chime.start(now + i * 0.1);
      chime.stop(now + i * 0.1 + 0.6);
    });

    // Frozen wind
    const windLength = ctx.sampleRate * 1;
    const windBuffer = ctx.createBuffer(1, windLength, ctx.sampleRate);
    const windData = windBuffer.getChannelData(0);
    for (let i = 0; i < windLength; i++) {
      windData[i] = (Math.random() * 2 - 1) * 0.3;
    }

    const wind = ctx.createBufferSource();
    wind.buffer = windBuffer;
    const windGain = ctx.createGain();
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = "bandpass";
    windFilter.frequency.setValueAtTime(4000, now);
    windFilter.Q.value = 3;
    
    windGain.gain.setValueAtTime(0, now);
    windGain.gain.linearRampToValueAtTime(0.08, now + 0.2);
    windGain.gain.exponentialRampToValueAtTime(0.001, now + 1);
    
    wind.connect(windFilter);
    windFilter.connect(windGain);
    windGain.connect(masterGain);
    wind.start(now);
    wind.stop(now + 1.1);

    // Ice crack
    const crack = ctx.createOscillator();
    const crackGain = ctx.createGain();
    crack.type = "triangle";
    crack.frequency.setValueAtTime(1500, now + 0.3);
    crack.frequency.exponentialRampToValueAtTime(100, now + 0.35);
    crackGain.gain.setValueAtTime(0, now + 0.3);
    crackGain.gain.linearRampToValueAtTime(0.15, now + 0.305);
    crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    crack.connect(crackGain);
    crackGain.connect(masterGain);
    crack.start(now + 0.3);
    crack.stop(now + 0.5);

    triggerHaptic("celebration");
  }, [getAudioContext, createReverb, triggerHaptic]);

  // Play themed celebration sound
  const playThemedCelebration = useCallback((theme: CelebrationTheme) => {
    switch (theme) {
      case "gold":
        playGoldCelebration();
        break;
      case "neon":
        playNeonCelebration();
        break;
      case "cosmic":
        playCosmicCelebration();
        break;
      case "fire":
        playFireCelebration();
        break;
      case "ice":
        playIceCelebration();
        break;
      case "classic":
      default:
        playCelebrationSound();
        break;
    }
  }, [playCelebrationSound, playGoldCelebration, playNeonCelebration, playCosmicCelebration, playFireCelebration, playIceCelebration]);

  const playSound = useCallback((type: SoundType) => {
    switch (type) {
      case "achievement":
        playAchievementSound();
        break;
      case "celebration":
        playCelebrationSound();
        break;
      case "success":
        playSuccessSound();
        break;
      case "pop":
        playPopSound();
        break;
      case "fanfare":
        playCelebrationSound();
        break;
      case "bail":
        playBailSound();
        break;
      case "ghost":
        playGhostSound();
        break;
      case "excuse":
        playExcuseSound();
        break;
      case "whoosh":
        playWhooshSound();
        break;
      case "click":
        playPopSound();
        break;
      case "notification":
        playNotificationSound();
        break;
    }
  }, [playAchievementSound, playCelebrationSound, playSuccessSound, playPopSound, playBailSound, playGhostSound, playExcuseSound, playWhooshSound, playNotificationSound]);

  return {
    playSound,
    triggerHaptic,
    playAchievementSound,
    playCelebrationSound,
    playSuccessSound,
    playPopSound,
    playBailSound,
    playGhostSound,
    playExcuseSound,
    playWhooshSound,
    playNotificationSound,
    playThemedCelebration,
  };
};

export default useSoundEffects;
